import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios.config';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatDate = (s) => {
    if (!s) return null;
    try {
        if (typeof s === 'string' && s.includes('/')) {
            const parts = s.split('/');
            if (parts.length === 3) {
                const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                if (!isNaN(d.getTime())) {
                    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                }
            }
        }
        return s;
    } catch {
        return s;
    }
};

const MobileBureauLookupOutputComponent = ({ reportData, pdfDownloadUrl, serviceDetails, hideCredits, isSharedView }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [downloadingPDF, setDownloadingPDF] = useState(false);

    const parsedData =
        typeof reportData === 'string'
            ? (() => {
                  try { return JSON.parse(reportData); } catch { return reportData; }
              })()
            : reportData;

    const pdfPasswordHint =
        parsedData?.pdf_password_hint ||
        reportData?.pdf_password_hint ||
        parsedData?.data?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        null;

    const remainingBalance =
        parsedData?.data?.remaining_balance ||
        parsedData?.remaining_balance ||
        serviceDetails?.available_credits;

    let personalInfo = {};
    let documentData = {};
    let addresses = [];
    let alternatePhones = [];
    let emails = [];

    try {
        if (reportData?.data?.response) {
            const r = reportData.data.response;
            personalInfo = r.personal_information || {};
            documentData = r.document_data || {};
            addresses = r.address || [];
            alternatePhones = r.alternate_phone || [];
            emails = r.email || [];
        } else if (reportData?.response) {
            const r = reportData.response;
            personalInfo = r.personal_information || {};
            documentData = r.document_data || {};
            addresses = r.address || [];
            alternatePhones = r.alternate_phone || [];
            emails = r.email || [];
        }
    } catch {
        // ignore
    }

    const status = reportData?.data?.status || reportData?.status || {};
    const reportId = reportData?.report_id || reportData?.data?.report_id;
    const pdfUrl =
        pdfDownloadUrl ||
        reportData?.pdf_download_url ||
        reportData?.pdf_url ||
        reportData?.data?.pdf_download_url ||
        reportData?.data?.pdf_url;

    if (!personalInfo || Object.keys(personalInfo).length === 0) {
        return <NoRecordsCard subtitle="No bureau data available for the provided mobile number." />;
    }

    const handleDownloadPDF = async () => {
        if (!pdfUrl && !reportId) {
            toast.error('PDF download URL not available');
            return;
        }
        if (pdfPasswordHint) {
            toast.info(pdfPasswordHint, { position: 'bottom-right', autoClose: 8000 });
        }

        setDownloadingPDF(true);
        try {
            toast.info('Preparing PDF download...');
            let downloadUrl = pdfUrl;
            if (!downloadUrl && reportId) {
                const r = await axiosInstance.get(`/api/tartan/mobile-bureau/${reportId}/generate-pdf`);
                if (r.data.success) downloadUrl = r.data.pdf_url || r.data.download_url;
            }
            if (downloadUrl) {
                if (downloadUrl.startsWith('http')) {
                    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                    toast.success('PDF download started');
                } else {
                    const r = await axiosInstance.get(downloadUrl, {
                        responseType: 'blob',
                        headers: { Accept: 'application/pdf' },
                    });
                    const blob = new Blob([r.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `mobile_bureau_lookup_${reportId || Date.now()}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('PDF downloaded successfully');
                }
            } else {
                toast.error('Unable to generate PDF download link');
            }
        } catch {
            toast.error('Failed to download PDF. Please try again.');
        } finally {
            setDownloadingPDF(false);
        }
    };

    const addressTypeBody = (row) => {
        const t = row.type || 'Other';
        let severity = 'info';
        if (t === 'Permanent') severity = 'success';
        else if (t === 'Current') severity = 'warning';
        return <Tag value={t} severity={severity} />;
    };
    const reportedOnBody = (row) => formatDate(row.date_of_reporting) || '—';

    return (
        <div>
            <SmartHeader title="Mobile Bureau Verification Report" icon="pi-mobile" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            <Card>
                <Section icon="pi-user" title="Personal Details">
                    <SmartField name="full_name" value={personalInfo.full_name} />
                    <SmartField name="gender" value={personalInfo.gender} />
                    {personalInfo.age && <SmartField name="age" value={`${personalInfo.age} years`} />}
                    <SmartField name="date_of_birth" value={formatDate(personalInfo.date_of_birth)} />
                    {personalInfo.income && <SmartField name="income" value={`₹${Number(personalInfo.income).toLocaleString('en-IN')}`} />}
                </Section>

                <Section icon="pi-phone" title="Contact Information">
                    <SmartField name="primary_mobile" value={status.input?.mobile} highlight />
                    {alternatePhones.length > 0 && (
                        <SmartField name="alternate_phones" value={alternatePhones.join(', ')} />
                    )}
                    {emails.length > 0 && (
                        <div className="col-12 flex flex-column gap-1">
                            <span className="text-500 text-xs font-medium">Email Addresses</span>
                            <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>{emails.join(', ')}</span>
                        </div>
                    )}
                </Section>

                <Section icon="pi-id-card" title="Document Information">
                    {documentData.pan?.length > 0 && <SmartField name="pan_cards" value={documentData.pan.join(', ')} />}
                    {documentData.aadhaar?.length > 0 && <SmartField name="aadhaar_cards" value={documentData.aadhaar.join(', ')} />}
                    {documentData.voter_id?.length > 0 && <SmartField name="voter_ids" value={documentData.voter_id.join(', ')} />}
                    {documentData.driving_license?.length > 0 && <SmartField name="driving_licenses" value={documentData.driving_license.join(', ')} />}
                </Section>

                {addresses.length > 0 && (
                    <Section icon="pi-map-marker" title={`Addresses (${addresses.length})`}>
                        <div className="col-12">
                            <DataTable
                                value={addresses}
                                size="small"
                                stripedRows
                                showGridlines
                                responsiveLayout="scroll"
                                paginator={addresses.length > 10}
                                rows={10}
                                className="p-datatable-sm p-datatable-gridlines"
                            >
                                <Column field="type" header="Type" body={addressTypeBody} style={{ width: '110px' }} />
                                <Column field="detailed_address" header="Address" />
                                <Column field="state" header="State" style={{ width: '130px' }} />
                                <Column field="pincode" header="Pincode" style={{ width: '110px' }} />
                                <Column field="date_of_reporting" header="Reported On" body={reportedOnBody} style={{ width: '140px' }} />
                            </DataTable>
                        </div>
                    </Section>
                )}

                <div className="text-center pt-3">
                    <Button
                        label="Download Mobile Bureau Lookup Report"
                        icon="pi pi-download"
                        onClick={handleDownloadPDF}
                        loading={downloadingPDF}
                        disabled={!pdfUrl && !reportId}
                        size="small"
                    />
                    {pdfPasswordHint && (
                        <div className="mt-2">
                            <Message severity="info" text={pdfPasswordHint} />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default MobileBureauLookupOutputComponent;
